import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useAuth } from '../context/AuthContext';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentViewerDialogProps {
  open: boolean;
  onClose: () => void;
  fetchUrl: string;
  title: string;
  subtitle?: string;
  initialPage?: number;
}

export default function DocumentViewerDialog({
  open,
  onClose,
  fetchUrl,
  title,
  subtitle,
  initialPage,
}: DocumentViewerDialogProps) {
  const { authFetch } = useAuth();
  const { t } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);

  const cleanup = useCallback(() => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setNumPages(null);
    setError(false);
  }, [pdfUrl]);

  useEffect(() => {
    if (!open || !fetchUrl) return;
    let cancelled = false;

    async function fetchDocument() {
      setLoading(true);
      setError(false);
      try {
        const res = await authFetch(fetchUrl);
        if (!res.ok || cancelled) {
          if (!cancelled) setError(true);
          return;
        }
        const blob = await res.blob();
        if (!cancelled) {
          setPdfUrl(URL.createObjectURL(blob));
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDocument();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchUrl]);

  const handleClose = () => {
    cleanup();
    onClose();
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${title}.pdf`;
    link.click();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: '#330662',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography component="span" variant="h6">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ opacity: 0.85 }}>{subtitle}</Typography>
          )}
        </Box>
        {pdfUrl && (
          <Button
            variant="contained"
            color="inherit"
            onClick={handleDownload}
            sx={{ color: '#330662' }}
          >
            {t('form.downloadPdf', 'Download PDF')}
          </Button>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#f5f5f5' }}>
        {loading ? (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
            {t('form.documentLoadError', 'Failed to load document. Please try again later.')}
          </Typography>
        ) : pdfUrl ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              '& canvas': { maxWidth: '100%', height: 'auto !important' },
            }}
          >
            <Document
              file={pdfUrl}
              onLoadSuccess={(pdf) => setNumPages(pdf.numPages)}
            >
              {initialPage ? (
                <Box sx={{ mb: 1 }}>
                  <Page pageNumber={initialPage} />
                </Box>
              ) : (
                Array.from(new Array(numPages || 1), (_, index) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Page pageNumber={index + 1} />
                  </Box>
                ))
              )}
            </Document>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t('form.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
